import React, { useState } from 'react';
import { Drawer, SIZE as DRAWER_SIZE } from 'baseui/drawer';
import { Button, KIND, SIZE } from 'baseui/button';
import { Payment, PaymentStatus } from '../types/payment.types';
import { formatPaymentDate, formatCurrency, formatCardNumber, canVoidPayment, paymentBusinessId } from '../utils/paymentUtils';
import { styled } from 'baseui';
import { Link } from 'react-router-dom';
import { passDetailLink } from '../api/businessAccessCombined';
import { AlertTriangle, XCircle, ArrowLeft, Lock, RotateCcw } from 'lucide-react';
import { useCapabilities } from '../hooks/useCapabilities';
import { useBusinessContext } from '../context/BusinessContext';
import { REFUND_PAYMENT, VOID_PAYMENT } from '../utils/capabilities';
import { isVoidedPayment, refundBlock } from '../utils/refunds';
import { PaymentRefundHistory, PaymentRefundTotals } from './PaymentRefundHistory';

const DrawerContent = styled('div', {
  padding: '24px',
  display: 'flex',
  flexDirection: 'column',
  gap: '20px',
  height: '100%',
  maxWidth: '600px',
  margin: '0 auto',
  width: '100%',
});

const Section = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '0px',
});

const SectionTitle = styled('h3', {
  fontSize: '18px',
  fontWeight: 600,
  color: '#1F2937',
  margin: '0 0 20px 0',
});

const DetailRow = styled('div', {
  display: 'grid',
  gridTemplateColumns: '180px 1fr',
  gap: '16px',
  padding: '14px 0',
  borderBottom: '1px solid #F3F4F6',
  alignItems: 'center',
});

const Label = styled('span', {
  fontWeight: 500,
  color: '#6B7280',
  fontSize: '13px',
});

const Value = styled('span', {
  fontWeight: 600,
  color: '#1F2937',
  fontSize: '14px',
  wordBreak: 'break-all',
});

const STATUS_LABEL: Record<PaymentStatus, string> = {
  success: 'Success',
  failed: 'Failed',
  voided: 'Voided',
  partially_refunded: 'Partially refunded',
  refunded: 'Refunded',
};

const StatusBadge: React.FC<{ status: PaymentStatus }> = ({ status }) => {
  const isSuccess = status === 'success';
  const isFailed = status === 'failed';
  const isPartial = status === 'partially_refunded';

  return (
    <span
      style={{
        padding: '4px 12px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: 600,
        display: 'inline-block',
        backgroundColor: isSuccess ? '#D1FAE5' : isFailed ? '#FEE2E2' : isPartial ? '#FEF3C7' : '#E5E7EB',
        color: isSuccess ? '#065F46' : isFailed ? '#991B1B' : isPartial ? '#92400E' : '#374151',
      }}
    >
      {STATUS_LABEL[status] || status}
    </span>
  );
};

const WarningBox = styled('div', {
  backgroundColor: '#FEF3C7',
  border: '1px solid #FCD34D',
  borderRadius: '8px',
  padding: '16px',
  display: 'flex',
  gap: '12px',
  marginTop: '8px',
});

const WarningText = styled('div', {
  fontSize: '13px',
  color: '#92400E',
  lineHeight: '1.5',
});

const ButtonContainer = styled('div', {
  display: 'flex',
  gap: '12px',
  marginTop: '16px',
  paddingTop: '20px',
  borderTop: '1px solid #F3F4F6',
});

interface PaymentDetailDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  payment: Payment | null;
  onConfirmVoid: () => void;
  /** SAM-466: opens the refund dialog for this payment. */
  onRefund?: (payment: Payment) => void;
  isLoading?: boolean;
}

export const PaymentDetailDrawer: React.FC<PaymentDetailDrawerProps> = ({
  isOpen,
  onClose,
  payment,
  onConfirmVoid,
  onRefund,
  isLoading = false,
}) => {
  const [showConfirmation, setShowConfirmation] = useState(false);
  const { hasCapability, isLoading: capabilitiesLoading } = useCapabilities();
  const { currentBusinessId } = useBusinessContext();

  const handleClose = () => {
    setShowConfirmation(false);
    onClose();
  };

  const handleVoidClick = () => {
    setShowConfirmation(true);
  };

  const handleConfirmVoid = () => {
    onConfirmVoid();
    setShowConfirmation(false);
  };

  const handleBack = () => {
    setShowConfirmation(false);
  };

  if (!payment) return null;

  const canVoid = canVoidPayment(payment);
  // SAM-544 (N03): void is its own backend capability (VOID_PAYMENT,
  // SAM-538/#342), independent of "can see the Payments page" — gate the
  // action by capability, not by role name, so the UI doesn't offer a void
  // the backend will 403. Fails closed while capabilities are still
  // loading or when the current business isn't resolved yet.
  const hasVoidCapability = !capabilitiesLoading && hasCapability(VOID_PAYMENT, paymentBusinessId(payment, currentBusinessId));
  // SAM-466: refund is REFUND_PAYMENT (groups `admin` tenant-wide, `business_admin` own business),
  // checked against the PAYMENT's business when the listing carries it (tenant-wide admin view).
  // Read-only payment viewers (e.g. `business_payments`) keep the details, not the action.
  const refundBusinessId = payment.business_id || currentBusinessId;
  const hasRefundCapability =
    !capabilitiesLoading && !!refundBusinessId && hasCapability(REFUND_PAYMENT, refundBusinessId);
  const refundBlocked = refundBlock(payment);
  // Void and Refund are never offered together: inside the void window the payment is voided; past it, only refunded.
  const showVoid = canVoid && hasVoidCapability;
  const showRefund = !!onRefund && !refundBlocked && hasRefundCapability && !showVoid;
  const showRefundFields =
    payment.refunded_amount !== undefined || (payment.refunds && payment.refunds.length > 0) || !!payment.refund_status;

  return (
    <Drawer
      isOpen={isOpen}
      autoFocus
      onClose={handleClose}
      size={DRAWER_SIZE.default}
      anchor="right"
      closeable={!isLoading}
      overrides={{
        Root: {
          style: {
            zIndex: 9999,
          },
        },
        DrawerContainer: {
          style: {
            zIndex: 10000,
          },
        },
        DrawerBody: {
          style: {
            marginTop: 0,
            marginBottom: 0,
            marginLeft: 0,
            marginRight: 0,
            padding: 0,
          },
        },
        Close: {
          style: {
            display: showConfirmation ? 'none' : 'block',
          },
        },
      }}
    >
      <DrawerContent>
        {showConfirmation ? (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Button
                kind={KIND.tertiary}
                size={SIZE.compact}
                onClick={handleBack}
                disabled={isLoading}
                overrides={{
                  BaseButton: {
                    style: {
                      paddingLeft: '8px',
                      paddingRight: '8px',
                    },
                  },
                }}
              >
                <ArrowLeft size={20} />
              </Button>
              <SectionTitle>Confirm Void Payment</SectionTitle>
            </div>

            <Section>
              <p style={{ margin: '0 0 16px 0', color: '#4B5563', fontSize: '14px' }}>
                Are you sure you want to void this payment?
              </p>

              <div>
                <DetailRow>
                  <Label>Reference:</Label>
                  <Value style={{ fontFamily: 'monospace' }}>{payment.payment_reference}</Value>
                </DetailRow>

                <DetailRow>
                  <Label>Amount:</Label>
                  <Value>{formatCurrency(payment.payment_amount)}</Value>
                </DetailRow>

                <DetailRow>
                  <Label>Card:</Label>
                  <Value>{formatCardNumber(payment.cardLast4)}</Value>
                </DetailRow>

                <DetailRow style={{ borderBottom: 'none' }}>
                  <Label>Date:</Label>
                  <Value>{formatPaymentDate(payment.payment_date)}</Value>
                </DetailRow>
              </div>

              <WarningBox>
                <AlertTriangle size={20} color="#D97706" style={{ flexShrink: 0, marginTop: '2px' }} />
                <WarningText>
                  <strong>IMPORTANT:</strong>
                  <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                    {/* The server cancels the pass of the payment in the same call (SAM-814). */}
                    {payment.business_access_id && <li>The pass of this payment will be cancelled too</li>}
                    <li>This action cannot be undone</li>
                  </ul>
                </WarningText>
              </WarningBox>
            </Section>

            <ButtonContainer>
              <Button
                kind={KIND.tertiary}
                onClick={handleBack}
                disabled={isLoading}
                overrides={{
                  BaseButton: {
                    style: {
                      flex: 1,
                    },
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmVoid}
                isLoading={isLoading}
                disabled={isLoading}
                overrides={{
                  BaseButton: {
                    style: {
                      flex: 1,
                      backgroundColor: 'var(--tenant-primary, #000)',
                      ':hover': {
                        backgroundColor: 'color-mix(in srgb, var(--tenant-primary, #000) 85%, black)',
                      },
                    },
                  },
                }}
              >
                Confirm Void
              </Button>
            </ButtonContainer>
          </>
        ) : (
          <>
            <SectionTitle>Payment Details</SectionTitle>

            <Section>
              <DetailRow>
                <Label>Payment Reference:</Label>
                <Value style={{ fontFamily: 'monospace' }}>{payment.payment_reference || 'N/A'}</Value>
              </DetailRow>

              <DetailRow>
                <Label>Transaction Reference:</Label>
                <Value style={{ fontFamily: 'monospace' }}>{payment.transaction_reference || 'N/A'}</Value>
              </DetailRow>

              <DetailRow>
                <Label>Status:</Label>
                <div>
                  {payment.payment_status ? (
                    <StatusBadge status={isVoidedPayment(payment) ? 'voided' : payment.payment_status} />
                  ) : (
                    <span style={{ color: '#6B7280' }}>N/A</span>
                  )}
                </div>
              </DetailRow>

              {payment.business_access_id && (
                <DetailRow>
                  <Label>Pass:</Label>
                  <Value>
                    {/* The payment's own page is this drawer; the pass is one click away (Passes opens its detail). */}
                    <Link to={passDetailLink(payment.business_access_id)} onClick={handleClose} style={{ fontWeight: 600 }}>
                      View pass
                    </Link>
                  </Value>
                </DetailRow>
              )}

              <DetailRow>
                <Label>Amount:</Label>
                <Value>{formatCurrency(payment.payment_amount)}</Value>
              </DetailRow>

              <DetailRow>
                <Label>Card:</Label>
                <Value>{formatCardNumber(payment.cardLast4)}</Value>
              </DetailRow>

              <DetailRow>
                <Label>Payment Date:</Label>
                <Value>{formatPaymentDate(payment.payment_date)}</Value>
              </DetailRow>

              <DetailRow style={{ borderBottom: 'none' }}>
                <Label>Created At:</Label>
                <Value>{formatPaymentDate(payment.createdAt)}</Value>
              </DetailRow>
            </Section>

            {showRefundFields && (
              <Section style={{ gap: '16px' }}>
                <PaymentRefundTotals payment={payment} />
                <PaymentRefundHistory payment={payment} />
              </Section>
            )}

            {refundBlocked === 'unresolved' && (
              <WarningBox data-testid="refund-unresolved">
                <AlertTriangle size={20} color="#D97706" style={{ flexShrink: 0, marginTop: '2px' }} />
                <WarningText>
                  A refund on this payment is pending or needs reconciliation. Do not send another one
                  until it is resolved.
                </WarningText>
              </WarningBox>
            )}

            {showRefund && (
              <ButtonContainer>
                <Button
                  kind={KIND.secondary}
                  onClick={() => onRefund(payment)}
                  startEnhancer={() => <RotateCcw size={16} />}
                  overrides={{ BaseButton: { props: { 'data-testid': 'payment-refund-button' }, style: { width: '100%' } } }}
                >
                  Refund
                </Button>
              </ButtonContainer>
            )}

            {showVoid && (
              <ButtonContainer>
                <Button
                  kind={KIND.primary}
                  onClick={handleVoidClick}
                  startEnhancer={() => <XCircle size={16} />}
                  overrides={{
                    BaseButton: {
                      style: {
                        width: '100%',
                        backgroundColor: 'var(--tenant-primary, #000)',
                        ':hover': {
                          backgroundColor: 'color-mix(in srgb, var(--tenant-primary, #000) 85%, black)',
                        },
                      },
                    },
                  }}
                >
                  Void Payment
                </Button>
              </ButtonContainer>
            )}

            {canVoid && !hasVoidCapability && !capabilitiesLoading && (
              <WarningBox>
                <Lock size={20} color="#D97706" style={{ flexShrink: 0, marginTop: '2px' }} />
                <WarningText>
                  Your account is not authorized to void payments for this business.
                </WarningText>
              </WarningBox>
            )}
          </>
        )}
      </DrawerContent>
    </Drawer>
  );
};
